import { faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import ClinicApi from "api/master/clinic.api";
import ReportApi from "api/report.api";
import VoucherApi from "api/voucher/voucher.api";
import HeaderCard from "component/CardCustom/HeaderCard";
import Loading from "component/Loading";
import _ from "lodash";
import DropdownButton from "new-components/buttons/dropdown-button";
import MainButton from "new-components/buttons/main-button";
import SelectInput from "new-components/inputs/select-input";
import TextInput from "new-components/inputs/text-input";
import PaginationTable from "new-components/pagination/pagination";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getBranch } from "utils/app.utils";
import { UseSnCode } from "utils/useSnCode";
import { exportGiftVoucherPatientToExcel, GiftVoucherReport } from "./export-to-excel-patient";


export default function GiftVoucherPatient() {
    const { t } = useTranslation()
    const componentRef = useRef<HTMLDivElement>(null);
    const [page, setPage] = useState(1)
    const [pageLimit, setPageLimit] = useState(10)
    const [rowCount, setRowCount] = useState(0)
    const [search, setSearch] = useState('')
    const [branch, setBranch] = useState<any>([])
    const [branchId, setBranchId] = useState<any>(getBranch())
    const [giftVoucherList, setGiftVoucherList] = useState([])
    const [giftVoucherExport, setGiftVoucherExport] = useState([])
    const [giftVoucherType, setGiftVoucherType] = useState<any>([])
    const [giftVoucherTypeId, setGiftVoucherTypeId] = useState("ALL")
    const [giftVoucherId, setGiftVoucherId] = useState("ALL")
    const [optionGiftVoucher, setOptionGiftVoucher] = useState<any>([])
    const [snType, setSnType] = useState(false)
    const [loading, setLoading] = useState<boolean>(true)

    const fetchGiftVoucherList = async () => {
        setLoading(true)
        const condition = {
            page: page,
            pageLimit: pageLimit,
            sortBy: 'approvalId',
            sortType: 'ASC',
            branchId: branchId,
            search: search,
            voucherTypeId: giftVoucherTypeId === "ALL" ? null : giftVoucherTypeId,
            voucherId: giftVoucherId === "ALL" ? null : giftVoucherId,
        }
        const resp: any = await ReportApi.reportGiftVoucherPatient(condition)
        const respExport: any = await ReportApi.reportGiftVoucherPatient({ ...condition, export: 1 })
        if (resp.status === 200) {
            setRowCount(resp?.headers['x-total'] || 0)
            setGiftVoucherList(resp.data?.rows)
            setGiftVoucherExport(respExport.data?.rows)
        } else {
            setGiftVoucherList([])
            setGiftVoucherExport([])
        }
        setLoading(false)
    }

    const fetchBranch = async () => {
        ClinicApi.findAllBranches()
            .then(({ data }) => setBranch(_.map(data, (item) => ({ value: item.branchId, name: item.branchName || item.branchNameEn })))).catch((e) => { return })
    }

    const fetchGiftVoucherType = async () => {
        VoucherApi.findAllType({
            page: 1,
            pageLimit: 100,
        }).then(({ data }) => setGiftVoucherType(_.map(data, (item) => ({ value: item.voucherTypeId, name: item.voucherTypeName })))).catch((e) => { return })
    }

    const fetchGiftVoucher = async () => {
        VoucherApi.findGiftVoucherList({
            voucherTypeId: giftVoucherTypeId === "ALL" ? null : giftVoucherTypeId
        }).then(({ data }) => setOptionGiftVoucher(_.map(data, (item) => ({ value: item.voucherId, name: item.voucherName })))).catch((e) => { return })
    }

    const handleSnCode = useCallback(() => {
        const fetchData = async () => {
            const sn = await UseSnCode()
            setSnType(sn)
        }
        fetchData()
    }, [snType])

    useEffect(() => {
        fetchGiftVoucherList()
    }, [page, pageLimit])


    useEffect(() => {
        fetchBranch()
        fetchGiftVoucherType()
        fetchGiftVoucher()
        handleSnCode()
    }, [])


    const handleExportExcel = () => {
        const report: GiftVoucherReport = {
            branchName: `${_.get(_.find(branch, { value: branchId }), 'name')}`,
            typeFilter: giftVoucherTypeId === "ALL" ? t('REPORT.FILTER.ALL') : `${_.get(_.find(giftVoucherType, { value: giftVoucherTypeId }), 'name')}`,
            giftFilter: giftVoucherId === "ALL" ? t('REPORT.FILTER.ALL') : `${_.get(_.find(optionGiftVoucher, { value: giftVoucherId }), 'name')}`,
            includeSn: snType,
            rows: giftVoucherExport.map((item: any) => ({
                cnNumber: item.cnNumber,
                branchCnNumber: item.branchCnNumber,
                snCode: item.snCode,
                patientName: item.patientName,
                voucherType: item.voucherTypeName,
                voucherName: item.voucherName,
                countWaiting: item.countWaiting,
                countReady: item.countReady,
                countUsed: item.countUsed,
                countExpired: item.countExpired
            })),
            t
        }
        exportGiftVoucherPatientToExcel(report)
    }
    const typeCounts = useMemo(() => {
        const map: Record<string, number> = {}
        giftVoucherList.forEach((item: any) => {
            const key = item.voucherTypeName || '-'
            map[key] = (map[key] || 0) + 1
        })
        return map
    }, [giftVoucherList])

    const seenTypes = useMemo(() => new Set<string>(), [])

    return (
        <Box>
            <Loading show={loading} />
            <HeaderCard text={t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.TITLE')} />
            <Box paddingX="24px" paddingY="16px">
                <Grid container spacing="16px">
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <TextInput
                            onChange={(e) => setSearch(e.target.value)}
                            fullWidth
                            label={t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.SEARCH')}
                            value={search}
                            inputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" >
                                        {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                onClick={() => {
                                                    setSearch('');
                                                }}
                                                style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <SelectInput onChange={(e) => setBranchId(e.target.value)} value={branchId} label={t('REPORT.FILTER.BRANCH')} options={branch} defaultValue={branch?.[0]?.branchId} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <SelectInput onChange={(e) => setGiftVoucherTypeId(e.target.value)} label={t('REPORT.FILTER.GIFT_VOUCHER_USED.TYPE')} options={[{ value: 'ALL', name: t('REPORT.FILTER.ALL') }, ...giftVoucherType]} defaultValue='ALL' />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <SelectInput onChange={(e) => setGiftVoucherId(e.target.value)} label={t('REPORT.FILTER.GIFT_VOUCHER_USED.PROMOTION')} options={[{ value: 'ALL', name: t('REPORT.FILTER.ALL') }, ...optionGiftVoucher]} defaultValue="ALL" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} xl={4}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <MainButton onClick={() => fetchGiftVoucherList()} title={t('INPUT.SEARCH')} type="primary" variant="contained" style={{ width: '120px' }} />
                            <DropdownButton title='พิมพ์เอกสาร' onChange={(e) => {
                                if (e === 'Excel') handleExportExcel()
                                else if (e === 'PDF') {
                                    const style = document.createElement("style");
                                    style.innerHTML = `
                                                        @media print {
                                                            @page { size: landscape }
                                                        }
                                                        `;
                                    document.head.appendChild(style);
                                    window.print();
                                    document.head.removeChild(style);
                                }
                            }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{ overflowX: 'auto', overflowY: 'hidden', marginTop: '16px' }}>
                    <TableContainer style={{ boxShadow: 'none', borderTopLeftRadius: '10px', borderTopRightRadius: '10px' }}>
                        <Table aria-label="simple table" sx={{ width: '100%' }}>
                            <TableHead sx={{ backgroundColor: "#2F4269", color: 'white', border: '1px solid #2F4269' }}>
                                <TableRow>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', minWidth: '100px', textAlign: 'center' }}>{t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.NO')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', minWidth: '100px', textAlign: 'center' }}>{t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.PATIENT_CN_NUMBER')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', minWidth: '160px', textAlign: 'center' }}>{t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.BRANCH_CN_NUMBER')}</TableCell>
                                    {snType && <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px' }}>{t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.PATIENT_SN_CODE')}</TableCell>}
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px' }}>{t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.PATIENT_NAME')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px' }}>{t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.GIFT_VOUCHER_TYPE')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px' }}>{t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.GIFT_VOUCHER')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', textAlign: 'center' }}>{t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.COUNT_WAITING')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', textAlign: 'center' }}>{t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.COUNT_READY')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', textAlign: 'center' }}>{t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.COUNT_USED')}</TableCell>
                                    <TableCell sx={{ color: "white", fontWeight: 500, fontSize: '16px', textAlign: 'center' }}>{t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.COUNT_EXPIRED')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {giftVoucherList.length === 0 ?
                                    <TableRow key={0} style={{ borderLeft: '1px solid rgba(224, 224, 224, 1)', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                                        <TableCell colSpan={11} sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{t('NOT_FOUND')}</TableCell>
                                    </TableRow>
                                    :
                                    giftVoucherList.map((data: any, index: number) => (
                                        <TableRow key={index} style={{ borderLeft: '1px solid rgba(224, 224, 224, 1)', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{page * pageLimit - pageLimit + index + 1}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{data?.cnNumber || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, textAlign: 'center' }}>{data?.branchCnNumber || "-"}</TableCell>
                                            {snType && <TableCell sx={{ fontSize: '16px', fontWeight: 400 }}>{data?.snCode || "-"}</TableCell>}
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, minWidth: '250px' }}>{data?.patientName || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, minWidth: '200px' }}>{data?.voucherTypeName || "-"}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, minWidth: '400px' }}>{data?.voucherName || '-'}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, minWidth: '150px', textAlign: 'center' }}>{data?.countWaiting || "0"}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, minWidth: '165px', textAlign: 'center' }}>{data?.countReady || "0"}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, minWidth: '150px', textAlign: 'center' }}>{data?.countUsed || "0"}</TableCell>
                                            <TableCell sx={{ fontSize: '16px', fontWeight: 400, minWidth: '150px', textAlign: 'center' }}>{data?.countExpired || "0"}</TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box marginTop="8px">
                        <PaginationTable
                            onChange={(_e, newPage) => setPage(newPage)}
                            onPerPageChange={(e) => {
                                setPageLimit(parseInt(e.target.value, 10))
                                setPage(1)
                            }}
                            page={page}
                            perPage={pageLimit}
                            totalPage={rowCount}
                        />
                    </Box>
                </Box>

                <Box id="print-table-container" ref={componentRef}>
                    <Typography fontSize="14px" fontWeight={400}>{t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.TITLE')}</Typography>
                    <Box display='flex' flexDirection="row" alignItems="center" gap="16px">
                        <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.BRANCH')}: {`${_.get(_.find(branch, { value: branchId }), 'name')}`}</Typography>
                        <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.GIFT_VOUCHER_USED.TYPE')}: {giftVoucherTypeId === "ALL" ? t('REPORT.FILTER.ALL') : `${_.get(_.find(giftVoucherType, { value: giftVoucherTypeId }), 'name')}`}</Typography>
                        <Typography fontSize="10px" fontWeight={400}>{t('REPORT.FILTER.GIFT_VOUCHER_USED.PROMOTION')}: {giftVoucherId === "ALL" ? t('REPORT.FILTER.ALL') : `${_.get(_.find(optionGiftVoucher, { value: giftVoucherId }), 'name')}`}</Typography>
                    </Box>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16, fontSize: 9.5 }}>
                        <thead>
                            <tr>
                                {[
                                    t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.NO'),
                                    t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.PATIENT_CN_NUMBER'),
                                    snType && t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.PATIENT_SN_CODE'),
                                    t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.PATIENT_NAME'),
                                    t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.GIFT_VOUCHER_TYPE'),
                                    t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.GIFT_VOUCHER'),
                                    t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.COUNT_WAITING'),
                                    t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.COUNT_READY'),
                                    t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.COUNT_USED'),
                                    t('REPORT.TABLE.GIFT_VOUCHER_PATIENT.COUNT_EXPIRED'),
                                ].filter(Boolean).map((h, i) => (
                                    <th key={i} style={{ fontSize: '10px', border: '1px solid black', padding: '6px', fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}> {h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {giftVoucherExport.length === 0 ? (
                                <tr>
                                    <td colSpan={11} style={{ border: '1px solid black', padding: '6px', fontSize: 14, verticalAlign: 'top', textAlign: 'center' }}>
                                        {t('NOT_FOUND')}
                                    </td>
                                </tr>
                            ) : (
                                giftVoucherExport.map((data: any, idx: number) => {
                                    const type = data.voucherTypeName || '-'
                                    const isFirst = !seenTypes.has(type)
                                    if (isFirst) seenTypes.add(type)

                                    return (
                                        <tr key={idx}>
                                            <td style={{ fontSize: '9.5px', border: '1px solid black', padding: '6px', verticalAlign: 'top', textAlign: 'left', fontWeight: 500 }}>{page * pageLimit - pageLimit + idx + 1}</td>
                                            <td style={{ fontSize: '9.5px', border: '1px solid black', padding: '6px', verticalAlign: 'top', textAlign: 'left', fontWeight: 500 }}>{data.cnNumber || '-'}</td>
                                            <td style={{ fontSize: '9.5px', border: '1px solid black', padding: '6px', verticalAlign: 'top', textAlign: 'left', fontWeight: 500 }}> {data.branchCnNumber || '-'} </td>
                                            {snType && (<td style={{ fontSize: '9.5px', border: '1px solid black', padding: '6px', verticalAlign: 'top', textAlign: 'left', fontWeight: 500 }}>{data.snCode || '-'} </td>)}
                                            <td style={{ fontSize: '9.5px', border: '1px solid black', padding: '6px', verticalAlign: 'top', textAlign: 'left', fontWeight: 500 }}>{data.patientName || '-'}</td>
                                            {isFirst && (<td rowSpan={typeCounts[type]} style={{ fontSize: '9.5px', border: '1px solid black', padding: '6px', verticalAlign: 'top', textAlign: 'left', fontWeight: 500 }} >{type} </td>)}
                                            <td style={{ fontSize: '9.5px', border: '1px solid black', padding: '6px', verticalAlign: 'top', textAlign: 'left', fontWeight: 500 }}>{data.voucherName || '-'}</td>
                                            <td style={{ fontSize: '9.5px', border: '1px solid black', padding: '6px', verticalAlign: 'top', textAlign: 'center', fontWeight: 500 }}>{data.countWaiting ?? '-'}</td>
                                            <td style={{ fontSize: '9.5px', border: '1px solid black', padding: '6px', verticalAlign: 'top', textAlign: 'center', fontWeight: 500 }}>{data.countReady ?? '-'}</td>
                                            <td style={{ fontSize: '9.5px', border: '1px solid black', padding: '6px', verticalAlign: 'top', textAlign: 'center', fontWeight: 500 }}>{data.countUsed ?? '-'}</td>
                                            <td style={{ fontSize: '9.5px', border: '1px solid black', padding: '6px', verticalAlign: 'top', textAlign: 'center', width: '50px' }}>{data.countExpired ?? '-'}</td>
                                        </tr>
                                    )
                                })
                            )}
                        </tbody>
                    </table>
                </Box>
            </Box>
        </Box>
    )
}